import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useSnackbar } from "notistack";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

import { useTranslation } from "react-i18next";
import { USER_ROLES_ARRAY } from "../constants";
import { useAuth } from "../features/auth/AuthContext";
import { useGetCurrentUserCargosQuery } from "../features/cargo/cargoApiSlice";

const POLLING_INTERVAL = 60_000; // 1 minute
const COMPANION_STATUS_TTL_MS = 30 * 60_000; // 30 minutes

const CargoTableContext = createContext();

export const CargoTableProvider = ({ children }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const { user } = useAuth();

	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: 50, // Number of rows per page
	});

	const [rowSelection, setRowSelection] = useState({});
	const [search, setSearch] = useState("");
	const [status, setStatus] = useState("sale");
	const [shouldLockRefetch, setShouldLockRefetch] = useState(false);

	const onStatusChange = useCallback((newStatus) => {
		setStatus(newStatus);
		setRowSelection([]);
		setFullSelectedRows([]);
		setSearch("");
		setPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));
	}, []);

	const {
		data: cargos,
		isLoading,
		isFetching,
		refetch,
	} = useGetCurrentUserCargosQuery(
		{
			status: status,
			page: pagination.pageIndex,
			perPage: pagination.pageSize,
			search: search,
		},
		{
			pollingInterval: shouldLockRefetch ? 0 : POLLING_INTERVAL,
			skipPollingIfUnfocused: true,
			skip: !user || !USER_ROLES_ARRAY.includes(user.role),
		},
	);

	const [prevCargos, setPrevCargos] = useState(null);
	const [cargosWithNewOffers, setCargosWithNewOffers] = useState([]);
	const [transCompanionStatusByCargoId, setTransCompanionStatusByCargoId] =
		useState({});
	const [timocomCompanionStatusByCargoId, setTimocomCompanionStatusByCargoId] =
		useState({});

	const setTransCompanionStatusForCargo = (cargoId, status) => {
		if (cargoId === undefined || cargoId === null) return;
		setTransCompanionStatusByCargoId((prev) => ({
			...prev,
			[cargoId]: { status, updatedAt: Date.now() },
		}));
	};

	const setTransCompanionStatusForCargos = (cargoIds, status) => {
		if (!Array.isArray(cargoIds) || cargoIds.length < 1) return;
		setTransCompanionStatusByCargoId((prev) => {
			const now = Date.now();
			const next = { ...prev };
			cargoIds.forEach((id) => {
				if (id === undefined || id === null) return;
				next[id] = { status, updatedAt: now };
			});
			return next;
		});
	};

	const setTimocomCompanionStatusForCargo = (cargoId, status) => {
		if (cargoId === undefined || cargoId === null) return;
		setTimocomCompanionStatusByCargoId((prev) => ({
			...prev,
			[cargoId]: { status, updatedAt: Date.now() },
		}));
	};

	const setTimocomCompanionStatusForCargos = (cargoIds, status) => {
		if (!Array.isArray(cargoIds) || cargoIds.length < 1) return;
		setTimocomCompanionStatusByCargoId((prev) => {
			const now = Date.now();
			const next = { ...prev };
			cargoIds.forEach((id) => {
				if (id === undefined || id === null) return;
				next[id] = { status, updatedAt: now };
			});
			return next;
		});
	};

	const [fullSelectedRows, setFullSelectedRows] = useState([]);

	const checkNewOffers = useCallback(
		(newCargos = cargos) => {
			if (prevCargos && newCargos && prevCargos.data && newCargos.data) {
				const newOffers = newCargos.data.reduce((acc, cargo) => {
					const prevCargo = prevCargos.data.find((pc) => pc.id === cargo.id);
					if (prevCargo && cargo.offers.length > prevCargo.offers.length) {
						acc.push(cargo.id);
						enqueueSnackbar(t("you_have_new_propositions_to_cargos"), {
							variant: "success",
							preventDuplicate: true,
							persist: true,
							action: (key) => {
								return (
									<IconButton
										onClick={() => closeSnackbar(key)}
										aria-label="close"
										color="primary"
										size="small"
									>
										<CloseIcon fontSize="small" />
									</IconButton>
								);
							},
						});
					}
					return acc;
				}, []);

				setCargosWithNewOffers((prevState) => [
					...new Set([...prevState, ...newOffers]),
				]);
			}
		},
		[cargos, closeSnackbar, enqueueSnackbar, prevCargos, t],
	);

	useEffect(() => {
		if (!isLoading && isFetching) {
			setPrevCargos(cargos);
		}
	}, [isLoading, isFetching, cargos]);

	useEffect(() => {
		checkNewOffers();
	}, [cargos, checkNewOffers]);

	const pruneStatusMap = useCallback((prevMap, cargosData, postedAtField) => {
		const now = Date.now();
		let changed = false;
		const next = { ...prevMap };

		for (const [id, entry] of Object.entries(next)) {
			const updatedAt = entry?.updatedAt;
			if (
				typeof updatedAt === "number" &&
				now - updatedAt > COMPANION_STATUS_TTL_MS
			) {
				delete next[id];
				changed = true;
			}
		}

		if (Array.isArray(cargosData)) {
			for (const cargo of cargosData) {
				if (cargo?.[postedAtField] && next[cargo.id]) {
					delete next[cargo.id];
					changed = true;
				}
			}
		}

		return changed ? next : prevMap;
	}, []);

	// Prune on cargos updates
	useEffect(() => {
		if (!cargos?.data) return;

		setTransCompanionStatusByCargoId((prev) =>
			pruneStatusMap(prev, cargos.data, "transPostedAt"),
		);
		setTimocomCompanionStatusByCargoId((prev) =>
			pruneStatusMap(prev, cargos.data, "timocomPostedAt"),
		);
	}, [cargos, pruneStatusMap]);

	// useEffect(() => {
	// 	if (cargosWithNewOffers.length < 1) {
	// 		closeSnackbar();
	// 	}
	// }, [cargosWithNewOffers]);

	const handleRemoveSingle = (id) => {
		setCargosWithNewOffers((prevState) => prevState.filter((i) => i !== id));
	};

	const value = useMemo(
		() => ({
			cargos,
			isLoading,
			isFetching,
			refetch,
			pagination,
			setPagination,
			rowSelection,
			setRowSelection,
			search,
			setSearch,
			status,
			onStatusChange,
			newOffersIndexes: cargosWithNewOffers,
			handleRemoveSingleRedDot: handleRemoveSingle,
			setShouldLockRefetch,
			transCompanionStatusByCargoId,
			setTransCompanionStatusForCargo,
			setTransCompanionStatusForCargos,
			timocomCompanionStatusByCargoId,
			setTimocomCompanionStatusForCargo,
			setTimocomCompanionStatusForCargos,
			fullSelectedRows,
			setFullSelectedRows,
		}),
		[
			cargos,
			isLoading,
			isFetching,
			refetch,
			pagination,
			rowSelection,
			search,
			status,
			onStatusChange,
			cargosWithNewOffers,
			transCompanionStatusByCargoId,
			timocomCompanionStatusByCargoId,
			fullSelectedRows,
		],
	);

	return (
		<CargoTableContext.Provider value={value}>
			{children}
		</CargoTableContext.Provider>
	);
};

export const useCargosTable = () => useContext(CargoTableContext);
